import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'
import { LatencyMetric } from '@amzn/ihm-associate-tools-js-lib'
import { List } from 'lodash'

declare let cordova: any

/**
 * Service that encapsulates cordova plugin calls for discoverability and common metrics.
 */
const candyCanePlugins = {
    BLUETOOTH: 'Bluetooth',
    WAKE_WORD_DETECTOR: 'WakeWordDetector',
    ENVIRONMENT_CLIENT: 'EnvironmentClient',
}

/**
 * Calls the hello world operation for the bluetooth plugin.
 * @return {Promise} - success when the plugin returns hello world.
 */
export const bluetoothHelloWorld = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'helloBluetoothPlugin', args)
}

/**
 * Calls the pair operation for the bluetooth plugin.
 * @return {Promise} - success when the plugin pairs device.
 */
export const bluetoothPair = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'pair', args)
}

/**
 * Calls the pairWithOptions operation for the bluetooth plugin.
 * @return {Promise} - success when the plugin pairs device.
 */
export const bluetoothPairWithOptions = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'pairWithOptions', args)
}

/**
 * Calls the connect operation for the bluetooth plugin.
 * @return {Promise} - success when the plugin connects to the device.
 */
export const bluetoothConnect = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'connect', args)
}

/**
 * Calls the getDeviceInfo operation for the bluetooth plugin.
 * @return {Promise} - success when the plugin gets device information.
 */
export const bluetoothGetDeviceInfo = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'getDeviceInfo', args)
}

/**
 * Calls the print operation for the bluetooth plugin.
 * @return {Promise} - success when the plugin prints.
 */
export const bluetoothPrint = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'print', args)
}

/**
 * sends data to the connected device.
 * @return {Promise} - success when the plugin prints.
 */
export const bluetoothWriteDataSerialComm = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'writeDataSerialComm', args)
}

/**
 * Sends a command to BT device with a given mac address and returns the response received from BT device.
 * @return {Promise} - success when the plugin prints.
 */
export const bluetoothRequestDataSerialComm = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'requestDataSerialComm', args)
}

/**
 * Closes bluetooth socket connection.
 * @return {Promise} - success when the plugin prints.
 */
export const bluetoothDisconnectSerialComm = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'disconnectSerialComm', args)
}

/**
 * Changes standard charset.
 * @return {Promise} - success when the plugin prints.
 */
export const bluetoothChangeCharsetSerialComm = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'changeCharsetSerialComm', args)
}

/**
 * Calls the connectBle operation for the bluetooth plugin.
 * @return {Promise} - success when the plugin connects to the BLE device.
 */
export const bluetoothConnectBle = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'connectBle', args)
}

/**
 * Closes bluetooth gatt connection from BLE device.
 * @return {Promise} - success when the plugin disconnects from the BLE device.
 */
export const bluetoothDisconnectBle = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'disconnectBle', args)
}

/**
 * Reads the Characteristic value from a BLE device with a given mac address and returns the response.
 * @return {Promise} - success when the plugin reads data from the BLE device.
 */
export const bluetoothReadCharacteristicBle = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'readCharacteristicBle', args)
}

/**
 * Writes the Characteristic value to a BLE device.
 * @return {Promise} - success when the plugin writes data to the BLE device.
 */
export const bluetoothWriteCharacteristicBle = (args: any[]): Promise<any> => {
    return cordovaExec(candyCanePlugins.BLUETOOTH, 'writeCharacteristicBle', args)
}

/**
 * Calls the initialize operation for the wakeword plugin.
 * @return {Promise} - Resolves if the initialize was successful and fails otherwise.
 */
export const wakeWordInitialize = (): Promise<any> => {
    return cordovaExec(candyCanePlugins.WAKE_WORD_DETECTOR, 'initializeWakeWord', [])
}

/**
 * Calls the start listening operation for the wakeword plugin
 * @return {Promise} Resolves if the start was successful and fails otherwise.
 */
export const wakeWordStart = (): Promise<any> => {
    return cordovaExec(candyCanePlugins.WAKE_WORD_DETECTOR, 'startWakeWordListening', [])
}

/**
 * Calls the stop listening operation for the wakeword plugin.
 * @return {Promise} Resolves if the stop was successful and fails otherwise.
 */
export const wakeWordStop = (): Promise<any> => {
    return cordovaExec(candyCanePlugins.WAKE_WORD_DETECTOR, 'stopWakeWordListening', [])
}

/**
 * Calls into the EnvironmentPlugin and gets the deviceDetails
 * @return {Promise} Resolves if successful with the device details.
 */
export const getDeviceDetails = (): Promise<any> => {
    return cordovaExec(candyCanePlugins.ENVIRONMENT_CLIENT, 'getDeviceDetails', [])
}

/**
 * Calls into the EnvironmentPlugin and gets the application (CandyCane)'s version.
 * @return {Promise} Resolves if successful with the version.
 */
export const getApplicationVersion = (): Promise<any> => {
    return cordovaExec(candyCanePlugins.ENVIRONMENT_CLIENT, 'getPackagesVersions', [])
        .then((response) => {
            return response && response.CandyCane || ''
        })
}

export const cordovaExec = (plugin: string, action: string, args: Object[]): Promise<any> => {

    const cordovaLatencyMetrics = new LatencyMetric(undefined, 'Time', 'IhmFlyingDutchman', 'candyCaneService.' + plugin + '.' + action)
    cordovaLatencyMetrics.start()

    const deferred = generateDeferredPromise<any>()
    cordova.exec(
        deferred.resolve,
        deferred.reject,
        plugin,
        action,
        args,
    )
    return deferred.promise.finally(() => {
        cordovaLatencyMetrics.stop()
    })
}

/**
 * Function to call Candycane plugin to perform given action.
 * @param {() => void} successCallback callback function when promise is resolved.
 * @param {() => void} failureCallback callback function when promise is rejected.
 * @param {string} plugin Name of the candycane plugin.
 * @param {string} action Action to be performed by the plugin.
 * @param {Object[]} args List of arguments passed to the plugin.
 * @returns {Promise} Resolves when action is successful.
 * Note: Promise never resolves when non promise function (successCallback, failureCallback) is passed as arguments.
 *       Kept this function separate from cordovaExec as cordovaExec() expects to be resolved every time.
 *       This function will resolve only when successCallback and failureCallback args are null.
 */

export const cordovaExecWithCallback = (successCallback: () => void, failureCallback: () => void,
                                        plugin: string, action: string, args: Object[]): Promise<any> => {
    const cordovaLatencyMetrics = new LatencyMetric(undefined, 'Time', 'IhmFlyingDutchman', 'candyCaneService.' + plugin + '.' + action)
    cordovaLatencyMetrics.start()

    const deferred = generateDeferredPromise<any>()
    let resolveFn = deferred.resolve
    let rejectFn = deferred.reject
    if (successCallback) { resolveFn = () => { successCallback(); deferred.resolve(null) } }
    if (failureCallback) { rejectFn = () => { failureCallback(); deferred.resolve(null) } }
    cordova.exec(
        resolveFn,
        rejectFn,
        plugin,
        action,
        args,
    )
    return deferred.promise.finally(() => { cordovaLatencyMetrics.stop() })
}
